import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
    FutureBlockCountResult,
    GetPlannedBlocksResult,
    LogPlannedBlockRequest,
    PlannedActionInfo,
    PlannedActionsResult,
    PlannedBlockRequest,
    PlannedBlockResult,
    PlannedBlockResultBase,
    PlannedBlockViewModel,
    PlannerAbsencesResult,
} from './planned-block.types';

// Thin wrapper over api/plannedblock/* (PlannedBlockController). Its own
// service (not folded into ActionService) since PlannedBlock is a separate
// backend entity from JobAction.
@Injectable({
    providedIn: 'root',
})
export class PlannedBlockService {
    constructor(
        private _http: HttpClient,
        @Inject('BASE_URL') private originUrl: string
    ) {}

    getPlannedBlocks(userId: string, dateFrom: Date, dateTo: Date): Observable<PlannedBlockViewModel[]> {
        const params = [
            `userId=${encodeURIComponent(userId)}`,
            `dateFrom=${encodeURIComponent(dateFrom.toISOString())}`,
            `dateTo=${encodeURIComponent(dateTo.toISOString())}`,
        ].join('&');

        return this._http
            .get<GetPlannedBlocksResult>(`${this.originUrl}api/plannedblock/planned-blocks?${params}`)
            .pipe(
                map((r) => {
                    if (!r.success) throw new Error(r.error);
                    return r.plannedBlocks || [];
                })
            );
    }

    /** One action's blocks (anyone's planner) from `from` on — the Actions
     *  list's ⋮ menu. */
    getActionBlocks(jobActionId: number, from: Date): Observable<PlannedBlockViewModel[]> {
        const params = `jobActionId=${jobActionId}&from=${encodeURIComponent(from.toISOString())}`;
        return this._http
            .get<GetPlannedBlocksResult>(`${this.originUrl}api/plannedblock/action-blocks?${params}`)
            .pipe(
                map((r) => {
                    if (!r.success) throw new Error(r.error);
                    return r.plannedBlocks || [];
                })
            );
    }

    /** Same as getPlannedBlocks, for several people in one request (each
     *  block carries its userId). */
    getPlannedBlocksForUsers(userIds: string[], dateFrom: Date, dateTo: Date): Observable<PlannedBlockViewModel[]> {
        return this._http
            .post<GetPlannedBlocksResult>(`${this.originUrl}api/plannedblock/planned-blocks-many`, {
                userIds,
                dateFrom: dateFrom.toISOString(),
                dateTo: dateTo.toISOString(),
            })
            .pipe(
                map((r) => {
                    if (!r.success) throw new Error(r.error);
                    return r.plannedBlocks || [];
                })
            );
    }

    /** Several people's holidays, bank holidays and non-working days in one
     *  request (HolidayController's planner-absences) — the Planner's grey days. */
    getPlannerAbsences(userIds: string[]): Observable<PlannerAbsencesResult> {
        return this._http
            .post<PlannerAbsencesResult>(`${this.originUrl}api/holiday/planner-absences`, { userIds })
            .pipe(
                map((r) => {
                    if (!r.success) throw new Error(r.error);
                    return r;
                })
            );
    }

    createPlannedBlock(request: PlannedBlockRequest): Observable<PlannedBlockViewModel> {
        return this._http
            .post<PlannedBlockResult>(`${this.originUrl}api/plannedblock/create-planned-block`, request)
            .pipe(
                map((r) => {
                    if (!r.success) throw new Error(r.error);
                    return r.plannedBlock;
                })
            );
    }

    updatePlannedBlock(id: number, request: PlannedBlockRequest): Observable<PlannedBlockViewModel> {
        return this._http
            .post<PlannedBlockResult>(`${this.originUrl}api/plannedblock/update-planned-block/${id}`, request)
            .pipe(
                map((r) => {
                    if (!r.success) throw new Error(r.error);
                    return r.plannedBlock;
                })
            );
    }

    // Creates a time entry from the block (api/plannedblock/log-planned-block),
    // linked to it so the block shows as Logged from then on. The response
    // also carries the new entry, which the Planner doesn't need.
    logPlannedBlock(id: number, request: LogPlannedBlockRequest): Observable<boolean> {
        return this._http
            .post<PlannedBlockResultBase>(`${this.originUrl}api/plannedblock/log-planned-block/${id}`, request)
            .pipe(
                map((r) => {
                    if (!r.success) throw new Error(r.error);
                    return true;
                })
            );
    }

    // Which of these actions have a block at or after `from` (and before
    // `to`, if given), and when their earliest such block starts.
    getPlannedActions(jobActionIds: number[], from: Date, to?: Date | null): Observable<PlannedActionInfo[]> {
        return this._http
            .post<PlannedActionsResult>(`${this.originUrl}api/plannedblock/planned-actions`, { jobActionIds, from, to: to ?? null })
            .pipe(
                map((r) => {
                    if (!r.success) throw new Error(r.error);
                    return r.plannedActions || [];
                })
            );
    }

    // How many of these actions' blocks start at or after `from` (the start
    // of tomorrow) — for the Completed/Archived prompt, see
    // PlannedBlockStatusPromptService.
    getFutureBlockCount(jobActionIds: number[], from: Date): Observable<number> {
        const params = [
            ...jobActionIds.map((id) => `jobActionIds=${id}`),
            `from=${encodeURIComponent(from.toISOString())}`,
        ].join('&');
        return this._http
            .get<FutureBlockCountResult>(`${this.originUrl}api/plannedblock/future-block-count?${params}`)
            .pipe(
                map((r) => {
                    if (!r.success) throw new Error(r.error);
                    return r.count;
                })
            );
    }

    // Removes those blocks (time already logged from them is kept, just
    // unlinked). Returns how many were removed.
    deleteFutureBlocks(jobActionIds: number[], from: Date): Observable<number> {
        return this._http
            .post<FutureBlockCountResult>(`${this.originUrl}api/plannedblock/delete-future-blocks`, { jobActionIds, from })
            .pipe(
                map((r) => {
                    if (!r.success) throw new Error(r.error);
                    return r.count;
                })
            );
    }

    deletePlannedBlock(id: number): Observable<boolean> {
        return this._http
            .post<PlannedBlockResultBase>(`${this.originUrl}api/plannedblock/delete-planned-block/${id}`, {})
            .pipe(
                map((r) => {
                    if (!r.success) throw new Error(r.error);
                    return true;
                })
            );
    }
}
